import { FC, useEffect, useState } from 'react';
import { AutoComplete, Avatar } from 'antd';
import { AxiosResponse } from 'axios';
import { capitalCase } from 'change-case';
import { TbChevronDown, TbInfoCircle, TbMailCode, TbWorldPin } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { PlanTag, SmallTooltip } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { PlatformForm as Form } from '@/shared/components/forms';

interface HeadquartersLocationProps {
    filters: any;
    handleChange: any;
}

const HeadquartersLocation: FC<HeadquartersLocationProps> = ({ filters, handleChange }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { hasMinimumPlan } = useEntitlements();

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [zipCodes, setZipCodes] = useState([]);

    const [selectedCountry, setSelectedCountry] = useState<string | null>(filters?.location?.country);
    const [selectedState, setSelectedState] = useState<string | null>(filters?.location?.stateTerritory);
    const [selectedCity, setSelectedCity] = useState<string | null>(filters?.location?.city);

    const { token } = useAntDesignTheme();

    useEffect(() => {
        // Fetch countries on mount
        authorizedAxiosInstance.get('/api/vendors/restcountries/all').then((response: AxiosResponse) => {
            if (response?.status === 200) {
                const countryOptions = response.data.map((country: any) => {
                    return {
                        value: country.cca2,
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    src={country.flags.png}
                                    icon={<TbWorldPin size='1rem' style={{ verticalAlign: '-0.15rem' }} />}
                                    shape='square'
                                    size='small'
                                    alt={country.name.common}
                                    style={{
                                        marginRight: '0.5rem',
                                        border: `1px solid ${token.colorBorder}`,
                                    }}
                                />
                                <span style={{ maxWidth: '3rem' }}>{country.name.common}</span>
                            </div>
                        ),
                        searchValue: country.name.common,
                    };
                });

                setCountries(countryOptions);
            }
        });
    }, []);

    const fetchCountryGeonameId = async (countryName: string) => {
        const response = await authorizedAxiosInstance.get('/api/vendors/geonames/searchJSON', {
            params: {
                q: countryName?.toLowerCase(),
                featureCode: 'PCLI', // This code stands for "political entity, a country"
                maxRows: 1,
            },
        });

        const country = response.data.geonames[0];

        return country;
    };

    const fetchStatesOrTerritories = async (country: any) => {
        const response = await authorizedAxiosInstance.get('/api/vendors/geonames/childrenJSON', {
            params: {
                geonameId: country?.geonameId,
            },
        });

        // console.log(response.data);

        const subdivisions = response.data.geonames.map((subdivision: any) => ({
            value: subdivision.adminCode1,
            label: (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        src={`http://flags.ox3.in/svg/${selectedCountry?.toLowerCase()}/${subdivision?.adminCodes1?.ISO3166_2?.toLowerCase()}.svg`}
                        icon={<TbWorldPin size='1rem' style={{ verticalAlign: '-0.15rem' }} />}
                        shape='square'
                        size='small'
                        alt={subdivision.name}
                        style={{
                            marginRight: '0.5rem',
                            border: `1px solid ${token.colorBorder}`,
                        }}
                    />
                    <span style={{ maxWidth: '3rem' }}>{subdivision.name}</span>
                </div>
            ),
            searchValue: subdivision.name,
            geonameId: subdivision.geonameId, // In case you need it for further queries
        }));

        return subdivisions;
    };

    useEffect(() => {
        if (selectedCountry) {
            // Fetch the geonameId for the selected country
            fetchCountryGeonameId(selectedCountry as string).then((country) => {
                if (country) {
                    // Fetch states/territories based on the country geonameId
                    fetchStatesOrTerritories(country).then((stateOptions) => {
                        setStates(stateOptions);
                    });
                }
            });
        }
    }, [selectedCountry]);

    useEffect(() => {
        if (selectedState) {
            // Fetch cities based on the selected state
            authorizedAxiosInstance
                .get(`/api/vendors/geonames/searchJSON`, {
                    params: {
                        adminCode1: selectedState,
                        // "a city, town, village, or other agglomeration of buildings where people live and work"
                        // featureCode: 'PPL', // https://www.geonames.org/export/codes.html
                    },
                })
                .then((response) => {
                    const cityOptions = response.data.geonames.map((city: any) => ({
                        value: city.name,
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    icon={<TbWorldPin size='1rem' style={{ verticalAlign: '-0.15rem' }} />}
                                    shape='square'
                                    size='small'
                                    alt={city.name}
                                    style={{
                                        marginRight: '0.5rem',
                                        border: `1px solid ${token.colorBorder}`,
                                    }}
                                />
                                <span style={{ maxWidth: '3rem' }}>{city.name && capitalCase(city.name)}</span>
                            </div>
                        ),
                        geonameId: city.geonameId,
                    }));

                    setCities(cityOptions);
                });
        }
    }, [selectedState]);

    useEffect(() => {
        if (selectedCity && selectedState) {
            // Fetch zip codes based on the selected city and state (US only)
            authorizedAxiosInstance
                .get(`/api/vendors/zippopotamus/${selectedCountry?.toLowerCase()}/${selectedState}/${selectedCity}`)
                .then((res: AxiosResponse) => {
                    if (res?.status === 200) {
                        const zipOptions = res?.data?.places.map((place: any) => ({
                            value: place['post code'],
                            label: (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar
                                        icon={<TbMailCode size='1rem' style={{ verticalAlign: '-0.15rem' }} />}
                                        shape='square'
                                        size='small'
                                        alt={place['post code']}
                                        style={{
                                            marginRight: '0.5rem',
                                            border: `1px solid ${token.colorBorder}`,
                                        }}
                                    />
                                    <span style={{ maxWidth: '3rem' }}>{place['post code']}</span>
                                </div>
                            ),
                        }));

                        setZipCodes(zipOptions);
                    }
                });
        }
    }, [selectedCity, selectedState]);

    return !hasMinimumPlan('plus') ? (
        <div style={{ textAlign: 'center', margin: '2rem' }}>
            <PlanTag plan={'plus'} style={{ marginLeft: '0.5rem' }} />
        </div>
    ) : (
        <>
            <PlatformAlert
                title='Location granularity'
                showIcon
                status='info'
                icon={<TbInfoCircle size='1.125rem' style={{ marginRight: '0.75rem', marginTop: '0.1rem' }} />}
                description={
                    <span>Search results may differ greatly based on the granularity of the options you choose.</span>
                }
                style={{ marginBottom: '0.75rem' }}
            />
            <Form layout='vertical'>
                <Form.Item label='Country' style={{ marginBottom: '0.5rem' }}>
                    <AutoComplete
                        disabled={loading}
                        options={countries}
                        onSelect={(value) => {
                            setSelectedCountry(value);
                            setSelectedState(null);
                            setSelectedCity(null);
                            setZipCodes([]);

                            handleChange(['location'], {
                                country: value,
                                stateTerritory: '',
                                city: '',
                                zipCode: '',
                            });
                        }}
                        placeholder='Select A Country...'
                        filterOption={(inputValue, option: any) =>
                            option!.searchValue.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                        }
                        suffixIcon={<TbChevronDown />}
                    />
                </Form.Item>
                <Form.Item label='State / Territory' style={{ marginBottom: '0.5rem' }}>
                    <AutoComplete
                        options={states}
                        onSelect={(value) => {
                            setSelectedState(value);
                            setSelectedCity(null);
                            setZipCodes([]);

                            handleChange(['location'], {
                                country: selectedCountry,
                                stateTerritory: value,
                                city: '',
                                zipCode: '',
                            });
                        }}
                        placeholder='Select A State Or Territory...'
                        disabled={!selectedCountry || loading}
                        filterOption={(inputValue, option: any) =>
                            option!.searchValue.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                        }
                        suffixIcon={<TbChevronDown />}
                    />
                </Form.Item>
                <Form.Item label='City' style={{ marginBottom: '0.5rem' }}>
                    <AutoComplete
                        options={cities}
                        onSelect={(value) => {
                            setSelectedCity(value);

                            handleChange(['location'], {
                                country: selectedCountry,
                                stateTerritory: selectedState,
                                city: value,
                                zipCode: '',
                            });
                        }}
                        placeholder='Select A City...'
                        disabled={!selectedState || loading}
                        filterOption={(inputValue, option: any) =>
                            option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                        }
                        suffixIcon={<TbChevronDown />}
                    />
                </Form.Item>
                <Form.Item
                    label={
                        <>
                            Zip Code
                            <SmallTooltip
                                title='Results may vary the most for jurisdictions outside of the United States.'
                                placement='right'
                                padding='0 0.375rem'
                            >
                                <TbInfoCircle
                                    style={{
                                        verticalAlign: '-0.1rem',
                                        marginLeft: '0.375rem',
                                        color: token.colorTextSecondary,
                                    }}
                                />
                            </SmallTooltip>
                        </>
                    }
                    style={{ marginBottom: '0.25rem' }}
                >
                    <AutoComplete
                        options={zipCodes}
                        placeholder='Select A ZIP Code...'
                        disabled={!selectedCity || loading}
                        filterOption={(inputValue, option: any) => option!.value.indexOf(inputValue) !== -1}
                        suffixIcon={<TbChevronDown />}
                        onSelect={(value) => {
                            handleChange(['location'], {
                                country: selectedCountry,
                                stateTerritory: selectedState,
                                city: selectedCity,
                                zipCode: value,
                            });
                        }}
                    />
                </Form.Item>
            </Form>
        </>
    );
};

export default HeadquartersLocation;
